import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchDecks, createDeck } from '../api/decks';
import {
  fetchWords,
  fetchWordDetail,
  createWord,
  updateWord,
  recordWordFamiliarity,
  setWordParked,
} from '../api/words';
import { createCard, updateCard, fetchCardCurve } from '../api/cards';
import { Deck } from '../types/deck';
import { Word, WordDetail, WordPayload, formatExampleText, getWordFamiliarityLevel } from '../types/word';
import { CardCurve } from '../types/card';
import { useToast } from '../context/ToastContext';
import { Modal } from '../components/Modal';
import { RetentionCurveChart } from '../components/RetentionCurveChart';
import {
  Plus,
  Search,
  Book,
  Volume2,
  FolderPlus,
  Star,
  Edit2,
  PauseCircle,
  PlayCircle,
  Layers,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileCode,
} from 'lucide-react';

export const WordbookPage: React.FC = () => {
  const { showToast } = useToast();

  const [decks, setDecks] = useState<Deck[]>([]);
  const [selectedDeckId, setSelectedDeckId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [searchRevision, setSearchRevision] = useState(0);
  const [words, setWords] = useState<Word[]>([]);
  const [totalWords, setTotalWords] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const pageSize = 30;
  const [loading, setLoading] = useState<boolean>(true);
  const [parkingFilter, setParkingFilter] = useState<'all' | 'active' | 'parked'>('all');
  const parkingFilterRef = useRef(parkingFilter);
  parkingFilterRef.current = parkingFilter;
  const [parkingWordId, setParkingWordId] = useState<string | null>(null);
  const [cardActionPending, setCardActionPending] = useState(false);
  const wordActionLockRef = useRef(false);
  const wordsRequestRef = useRef(0);
  const searchPendingRef = useRef(false);
  const decksRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const curveRequestRef = useRef(0);
  const detailWordIdRef = useRef<string | null>(null);
  const loadWordsRef = useRef<() => Promise<void>>(async () => {});

  // Modals state
  const [createDeckModalOpen, setCreateDeckModalOpen] = useState<boolean>(false);
  const [newDeckName, setNewDeckName] = useState<string>('');
  const [newDeckDesc, setNewDeckDesc] = useState<string>('');

  const [wordModalOpen, setWordModalOpen] = useState<boolean>(false);
  const [editingWordId, setEditingWordId] = useState<string | null>(null);
  const [editingOriginalWord, setEditingOriginalWord] = useState<Word | null>(null);
  const [editMode, setEditMode] = useState<'form' | 'json'>('form');
  const [fullJsonText, setFullJsonText] = useState<string>('');
  const [initialFormValues, setInitialFormValues] = useState<any>(null);

  // Form fields (partitioned)
  const [wordFormData, setWordFormData] = useState<{
    term: string;
    meaning: string;
    phonetic: string;
    deck_id: string;
    examplesText: string;
    tagsText: string;
    // Rich linguistics
    lemma: string;
    partsOfSpeechText: string;
    cefr: string;
    rootsText: string;
    // Relations & Mnemonics
    synonymsText: string;
    antonymsText: string;
    collocationsText: string;
    mnemonicsText: string;
    notes: string;
    // Custom JSON extra
    extraJson: string;
  }>({
    term: '',
    meaning: '',
    phonetic: '',
    deck_id: '',
    examplesText: '',
    tagsText: '',
    lemma: '',
    partsOfSpeechText: '',
    cefr: '',
    rootsText: '',
    synonymsText: '',
    antonymsText: '',
    collocationsText: '',
    mnemonicsText: '',
    notes: '',
    extraJson: '',
  });

  // Collapsible sections in editor
  const [showLinguistics, setShowLinguistics] = useState<boolean>(false);
  const [showRelations, setShowRelations] = useState<boolean>(false);
  const [showExtraJson, setShowExtraJson] = useState<boolean>(false);

  // Word detail & cards modal
  const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
  const [selectedWordDetail, setSelectedWordDetail] = useState<WordDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailTab, setDetailTab] = useState<'core' | 'linguistics' | 'relations' | 'json' | 'cards'>('core');
  const [activeCardCurve, setActiveCardCurve] = useState<CardCurve | null>(null);

  // Familiarity modal
  const [familiarityModalOpen, setFamiliarityModalOpen] = useState<boolean>(false);
  const [familiarityWord, setFamiliarityWord] = useState<Word | null>(null);
  const [familiarityLevel, setFamiliarityLevel] = useState<number>(3);
  const [familiarityNote, setFamiliarityNote] = useState<string>('');

  // Load Decks
  const loadDecks = useCallback(async () => {
    const requestId = ++decksRequestRef.current;
    try {
      const data = await fetchDecks();
      if (requestId === decksRequestRef.current) setDecks(data || []);
    } catch (err: any) {
      if (requestId === decksRequestRef.current) showToast(err.message || '加载词本列表失败', 'error');
    }
  }, [showToast]);

  // Load Words
  const loadWords = useCallback(async () => {
    if (searchPendingRef.current) return;
    const requestId = ++wordsRequestRef.current;
    setLoading(true);
    try {
      const data = await fetchWords({
        query: debouncedSearchQuery || undefined,
        deck_id: selectedDeckId || undefined,
        limit: pageSize,
        offset: page * pageSize,
        parked: parkingFilter === 'all' ? undefined : parkingFilter === 'parked',
      });
      if (requestId !== wordsRequestRef.current) return;
      setTotalWords(data.total || 0);
      const lastPage = Math.max(0, Math.ceil((data.total || 0) / pageSize) - 1);
      if (page > lastPage) {
        setPage(lastPage);
        return;
      }
      setWords(data.items || []);
    } catch (err: any) {
      if (requestId === wordsRequestRef.current) showToast(err.message || '加载生词失败', 'error');
    } finally {
      if (requestId === wordsRequestRef.current) setLoading(false);
    }
  }, [debouncedSearchQuery, searchRevision, selectedDeckId, page, parkingFilter, showToast]);
  loadWordsRef.current = loadWords;

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      if (!searchPendingRef.current) return;
      searchPendingRef.current = false;
      setDebouncedSearchQuery(searchQuery.trim());
      setPage(0);
      setSearchRevision((previous) => previous + 1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  const applyLocalParking = (wordId: string, parked: boolean) => {
    setWords((previous) => previous
      .map((item) => item.id === wordId ? { ...item, parked } : item)
      .filter((item) => parkingFilterRef.current === 'all'
        || Boolean(item.parked) === (parkingFilterRef.current === 'parked')));
  };

  const handleParking = async (word: Word) => {
    if (wordActionLockRef.current) return;
    wordActionLockRef.current = true;
    setParkingWordId(word.id);
    try {
      const result = await setWordParked(word.id, !word.parked);
      wordsRequestRef.current += 1;
      applyLocalParking(word.id, result.parked);
      setSelectedWordDetail((previous) => previous?.id === word.id ? {
        ...previous,
        parked: result.parked,
        cards: previous.cards?.map((card) => ({ ...card, suspended: result.parked })),
      } : previous);
      showToast(word.parked ? '已恢复学习，原记忆进度保留' : '已搁置，暂时不再进入学习队列', 'success');
      const results = await Promise.allSettled([loadWordsRef.current(), loadDecks(), refreshSelectedWord(word.id)]);
      if (results[2].status === 'rejected') showToast('状态已保存，详情同步失败，可重新打开查看', 'error');
    } catch (err: any) {
      showToast(err.message || '更新搁置状态失败', 'error');
    } finally {
      setParkingWordId(null);
      wordActionLockRef.current = false;
    }
  };

  useEffect(() => {
    loadDecks();
    return () => { decksRequestRef.current += 1; };
  }, [loadDecks]);

  useEffect(() => {
    loadWords();
    return () => { wordsRequestRef.current += 1; };
  }, [loadWords]);

  // Handle Create Deck
  const handleCreateDeck = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeckName.trim()) return;
    try {
      const created = await createDeck({
        name: newDeckName.trim(),
        description: newDeckDesc.trim() || undefined,
      });
      showToast(`词本「${created.name}」创建成功`, 'success');
      setCreateDeckModalOpen(false);
      setNewDeckName('');
      setNewDeckDesc('');
      await loadDecks();
    } catch (err: any) {
      showToast(err.message || '创建词本失败', 'error');
    }
  };

  // Helper: check if array contains purely strings
  const isStringArray = (arr: any) => Array.isArray(arr) && arr.every((x) => typeof x === 'string');

  // Open add/edit word modal
  const openWordModal = (word?: Word) => {
    setEditMode('form');
    if (word) {
      setEditingWordId(word.id);
      setEditingOriginalWord(word);

      // Prepare full JSON editor text (excluding read-only system columns)
      const cleanJson: Record<string, any> = { ...word };
      delete cleanJson.id;
      delete cleanJson.cards;
      delete cleanJson.familiarity;
      delete cleanJson.familiarity_note;
      delete cleanJson.deck_ids;
      delete cleanJson.created_at;
      delete cleanJson.updated_at;
      delete cleanJson.version;
      delete cleanJson.parked;
      delete cleanJson.card_ids;
      delete cleanJson.user_id;
      delete cleanJson.deleted_at;
      delete cleanJson.deck_name;
      setFullJsonText(JSON.stringify(cleanJson, null, 2));

      // For form text conversions, only convert to string lines if it is a pure string array
      const examplesText = isStringArray(word.examples) ? (word.examples as string[]).join('\n') : '';
      const tagsText = isStringArray(word.tags) ? (word.tags as string[]).join(', ') : '';
      const partsOfSpeechText = isStringArray(word.parts_of_speech) ? (word.parts_of_speech as string[]).join(', ') : '';
      const rootsText = isStringArray(word.roots) ? (word.roots as string[]).join(', ') : '';
      const synonymsText = isStringArray(word.synonyms) ? (word.synonyms as string[]).join(', ') : '';
      const antonymsText = isStringArray(word.antonyms) ? (word.antonyms as string[]).join(', ') : '';
      const collocationsText = isStringArray(word.collocations) ? (word.collocations as string[]).join('\n') : '';
      const mnemonicsText = isStringArray(word.mnemonics) ? (word.mnemonics as string[]).join('\n') : '';
      const extraJson = word.extra ? JSON.stringify(word.extra, null, 2) : '';

      const formData = {
        term: word.term || '',
        meaning: word.meaning || '',
        phonetic: word.phonetic || '',
        deck_id: word.deck_id || selectedDeckId || '',
        examplesText,
        tagsText,
        lemma: word.lemma || '',
        partsOfSpeechText,
        cefr: word.cefr || '',
        rootsText,
        synonymsText,
        antonymsText,
        collocationsText,
        mnemonicsText,
        notes: word.notes || '',
        extraJson,
      };

      setWordFormData(formData);
      setInitialFormValues(formData);
    } else {
      setEditingWordId(null);
      setEditingOriginalWord(null);
      setFullJsonText('{\n  "term": "",\n  "meaning": ""\n}');
      const initialEmpty = {
        term: '',
        meaning: '',
        phonetic: '',
        deck_id: selectedDeckId || (decks[0]?.id ?? ''),
        examplesText: '',
        tagsText: '',
        lemma: '',
        partsOfSpeechText: '',
        cefr: '',
        rootsText: '',
        synonymsText: '',
        antonymsText: '',
        collocationsText: '',
        mnemonicsText: '',
        notes: '',
        extraJson: '',
      };
      setWordFormData(initialEmpty);
      setInitialFormValues(initialEmpty);
    }
    setShowLinguistics(false);
    setShowRelations(false);
    setShowExtraJson(false);
    setWordModalOpen(true);
  };

  // Save Word (Create or Update with fine-grained delta to preserve all 136 properties)
  const handleSaveWord = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mode 1: Full JSON editor mode
    if (editMode === 'json') {
      let parsed: Record<string, any>;
      try {
        parsed = JSON.parse(fullJsonText);
      } catch (err: any) {
        showToast('JSON 格式解析错误：' + (err.message || '请检查标点与双引号'), 'error');
        return;
      }
      if (!parsed.term || typeof parsed.term !== 'string') {
        showToast('JSON 中必须包含非空的 term 键', 'warning');
        return;
      }

      try {
        if (editingWordId) {
          await updateWord(editingWordId, parsed, editingOriginalWord?.version);
          showToast('词条 JSON 全量结构已更新', 'success');
        } else {
          await createWord(parsed as WordPayload);
          showToast('生词已从完整 JSON 成功创建', 'success');
        }
        setWordModalOpen(false);
        loadWords();
        loadDecks();
      } catch (err: any) {
        showToast(err.message || '保存词条失败', 'error');
      }
      return;
    }

    // Mode 2: Quick Form Mode
    if (!wordFormData.term.trim()) {
      showToast('单词不能为空', 'warning');
      return;
    }

    try {
      if (editingWordId) {
        // Delta computation: ONLY submit keys that actually changed!
        const delta: Record<string, any> = {};

        if (wordFormData.term.trim() !== (initialFormValues?.term || '')) {
          delta.term = wordFormData.term.trim();
        }
        if (wordFormData.meaning.trim() !== (initialFormValues?.meaning || '')) {
          delta.meaning = wordFormData.meaning.trim();
        }
        if (wordFormData.phonetic.trim() !== (initialFormValues?.phonetic || '')) {
          delta.phonetic = wordFormData.phonetic.trim() || null;
        }
        if (wordFormData.deck_id !== (initialFormValues?.deck_id || '')) {
          delta.deck_id = wordFormData.deck_id || null;
        }
        if (wordFormData.lemma.trim() !== (initialFormValues?.lemma || '')) {
          delta.lemma = wordFormData.lemma.trim() || null;
        }
        if (wordFormData.cefr.trim() !== (initialFormValues?.cefr || '')) {
          delta.cefr = wordFormData.cefr.trim() || null;
        }
        if (wordFormData.notes.trim() !== (initialFormValues?.notes || '')) {
          delta.notes = wordFormData.notes.trim() || null;
        }
        if (wordFormData.tagsText !== initialFormValues?.tagsText) {
          delta.tags = wordFormData.tagsText.split(/[,，]/).map((s) => s.trim()).filter(Boolean);
        }
        if (wordFormData.partsOfSpeechText !== initialFormValues?.partsOfSpeechText) {
          delta.parts_of_speech = wordFormData.partsOfSpeechText.split(/[,，]/).map((s) => s.trim()).filter(Boolean);
        }

        // Only update array fields if user actually edited the text
        if (wordFormData.examplesText !== initialFormValues?.examplesText) {
          delta.examples = wordFormData.examplesText.split('\n').map((s) => s.trim()).filter(Boolean);
        }
        if (wordFormData.rootsText !== initialFormValues?.rootsText) {
          delta.roots = wordFormData.rootsText.split(/[,，]/).map((s) => s.trim()).filter(Boolean);
        }
        if (wordFormData.synonymsText !== initialFormValues?.synonymsText) {
          delta.synonyms = wordFormData.synonymsText.split(/[,，]/).map((s) => s.trim()).filter(Boolean);
        }
        if (wordFormData.antonymsText !== initialFormValues?.antonymsText) {
          delta.antonyms = wordFormData.antonymsText.split(/[,，]/).map((s) => s.trim()).filter(Boolean);
        }
        if (wordFormData.collocationsText !== initialFormValues?.collocationsText) {
          delta.collocations = wordFormData.collocationsText.split('\n').map((s) => s.trim()).filter(Boolean);
        }
        if (wordFormData.mnemonicsText !== initialFormValues?.mnemonicsText) {
          delta.mnemonics = wordFormData.mnemonicsText.split('\n').map((s) => s.trim()).filter(Boolean);
        }
        if (wordFormData.extraJson !== initialFormValues?.extraJson) {
          if (wordFormData.extraJson.trim()) {
            try {
              delta.extra = JSON.parse(wordFormData.extraJson.trim());
            } catch {
              showToast('自定义 Extra JSON 格式不合法', 'warning');
              return;
            }
          } else {
            delta.extra = {};
          }
        }

        if (Object.keys(delta).length === 0) {
          showToast('未检测到任何字段修改', 'info');
          setWordModalOpen(false);
          return;
        }

        await updateWord(editingWordId, delta, editingOriginalWord?.version);
        showToast(`已成功保存修改项: ${Object.keys(delta).join(', ')}`, 'success');
      } else {
        // Create new word
        let parsedExtra: Record<string, any> | undefined = undefined;
        if (wordFormData.extraJson.trim()) {
          try {
            parsedExtra = JSON.parse(wordFormData.extraJson.trim());
          } catch {
            showToast('自定义 Extra JSON 格式不合法', 'warning');
            return;
          }
        }

        const newPayload: WordPayload = {
          term: wordFormData.term.trim(),
          meaning: wordFormData.meaning.trim(),
          phonetic: wordFormData.phonetic.trim() || undefined,
          deck_id: wordFormData.deck_id || undefined,
          examples: wordFormData.examplesText.split('\n').map((s) => s.trim()).filter(Boolean),
          tags: wordFormData.tagsText.split(/[,，]/).map((s) => s.trim()).filter(Boolean),
          lemma: wordFormData.lemma.trim() || undefined,
          parts_of_speech: wordFormData.partsOfSpeechText.split(/[,，]/).map((s) => s.trim()).filter(Boolean),
          cefr: wordFormData.cefr.trim() || undefined,
          roots: wordFormData.rootsText.split(/[,，]/).map((s) => s.trim()).filter(Boolean),
          synonyms: wordFormData.synonymsText.split(/[,，]/).map((s) => s.trim()).filter(Boolean),
          antonyms: wordFormData.antonymsText.split(/[,，]/).map((s) => s.trim()).filter(Boolean),
          collocations: wordFormData.collocationsText.split('\n').map((s) => s.trim()).filter(Boolean),
          mnemonics: wordFormData.mnemonicsText.split('\n').map((s) => s.trim()).filter(Boolean),
          notes: wordFormData.notes.trim() || undefined,
          extra: parsedExtra,
        };

        await createWord(newPayload);
        showToast('生词添加成功，并已同步建立复习卡片', 'success');
      }

      setWordModalOpen(false);
      loadWords();
      loadDecks();
    } catch (err: any) {
      showToast(err.message || '保存单词失败', 'error');
    }
  };

  // Open Word Detail & Cards
  const openWordDetail = async (wordId: string) => {
    const requestId = ++detailRequestRef.current;
    detailWordIdRef.current = wordId;
    curveRequestRef.current += 1;
    setDetailLoading(true);
    setDetailTab('core');
    setDetailModalOpen(true);
    setActiveCardCurve(null);
    setSelectedWordDetail(null);
    try {
      const detail = await fetchWordDetail(wordId);
      if (requestId !== detailRequestRef.current || detailWordIdRef.current !== wordId) return;
      setSelectedWordDetail(detail);

      if (detail.cards && detail.cards.length > 0) {
        loadCardCurve(detail.cards[0].id);
      }
    } catch (err: any) {
      if (requestId === detailRequestRef.current) showToast(err.message || '加载词条详情失败', 'error');
    } finally {
      if (requestId === detailRequestRef.current) setDetailLoading(false);
    }
  };

  const closeWordDetail = () => {
    detailWordIdRef.current = null;
    detailRequestRef.current += 1;
    curveRequestRef.current += 1;
    setDetailModalOpen(false);
  };

  const refreshSelectedWord = async (wordId: string) => {
    if (detailWordIdRef.current !== wordId) return;
    const requestId = ++detailRequestRef.current;
    const detail = await fetchWordDetail(wordId);
    if (requestId === detailRequestRef.current && detailWordIdRef.current === wordId) {
      setSelectedWordDetail(detail);
    }
    return detail;
  };

  const loadCardCurve = async (cardId: string) => {
    const requestId = ++curveRequestRef.current;
    setActiveCardCurve(null);
    try {
      const curve = await fetchCardCurve(cardId);
      if (requestId === curveRequestRef.current) setActiveCardCurve(curve);
    } catch {
      if (requestId === curveRequestRef.current) setActiveCardCurve(null);
    }
  };

  // Toggle card suspension
  const handleToggleCardSuspend = async (cardId: string, currentSuspended?: boolean) => {
    if (!selectedWordDetail || wordActionLockRef.current) return;
    wordActionLockRef.current = true;
    setCardActionPending(true);
    const wordId = selectedWordDetail.id;
    try {
      const updatedCard = await updateCard(cardId, { suspended: !currentSuspended });
      const updatedCards = selectedWordDetail.cards?.map((card) => card.id === cardId ? { ...card, ...updatedCard } : card) || [];
      const parked = updatedCards.length > 0 && updatedCards.every((card) => card.suspended);
      setSelectedWordDetail((previous) => previous?.id === wordId ? { ...previous, cards: updatedCards, parked } : previous);
      applyLocalParking(wordId, parked);
      showToast(currentSuspended ? '卡片已恢复学习' : '卡片已搁置', 'info');
      const results = await Promise.allSettled([refreshSelectedWord(wordId), loadWordsRef.current(), loadDecks()]);
      if (results[0].status === 'rejected') showToast('状态已保存，详情同步失败，可重新打开查看', 'error');
    } catch (err: any) {
      showToast(err.message || '更新卡片状态失败', 'error');
    } finally {
      wordActionLockRef.current = false;
      setCardActionPending(false);
    }
  };

  // Add Card for Word
  const handleAddCardForWord = async (template: 'recognition' | 'production') => {
    if (!selectedWordDetail || wordActionLockRef.current) return;
    wordActionLockRef.current = true;
    setCardActionPending(true);
    const wordId = selectedWordDetail.id;
    try {
      const createdCard = await createCard({
        word_id: wordId,
        deck_id: selectedWordDetail.deck_id || decks[0]?.id || '',
        template,
      });
      setSelectedWordDetail((previous) => previous?.id === wordId ? {
        ...previous,
        parked: false,
        cards: [...(previous.cards || []).filter((card) => card.id !== createdCard.id), createdCard],
      } : previous);
      applyLocalParking(wordId, false);
      showToast('学习卡片已生成', 'success');
      const results = await Promise.allSettled([refreshSelectedWord(wordId), loadWordsRef.current(), loadDecks()]);
      if (results[0].status === 'rejected') showToast('卡片已生成，详情同步失败，可重新打开查看', 'error');
    } catch (err: any) {
      showToast(err.message || '生成卡片失败', 'error');
    } finally {
      wordActionLockRef.current = false;
      setCardActionPending(false);
    }
  };

  // Open Familiarity Modal
  const openFamiliarityModal = (word: Word) => {
    setFamiliarityWord(word);
    const lvl = getWordFamiliarityLevel(word);
    setFamiliarityLevel(lvl ?? 3);
    setFamiliarityNote(word.familiarity_note || '');
    setFamiliarityModalOpen(true);
  };

  // Save Familiarity Record
  const handleSaveFamiliarity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!familiarityWord) return;
    try {
      await recordWordFamiliarity(familiarityWord.id, {
        level: familiarityLevel,
        note: familiarityNote.trim() || undefined,
      });
      showToast('熟练度记录已保存（不影响卡片调度）', 'success');
      setFamiliarityModalOpen(false);
      loadWords();
    } catch (err: any) {
      showToast(err.message || '保存熟练度失败', 'error');
    }
  };

  // Native audio
  const playAudio = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'en-US';
      window.speechSynthesis.speak(u);
    }
  };

  const totalPages = Math.ceil(totalWords / pageSize) || 1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Top Controls: Deck filter, Search, Add word, Create deck */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">词本与生词管理</h2>
          <p className="text-xs text-slate-500 mt-1">
            共 {totalWords} 个单词 • 支持 60+ 词汇学字段与扩展数据分区管理，严谨完整
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={() => setCreateDeckModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-sm"
          >
            <FolderPlus className="w-4 h-4 text-slate-500" />
            新建词本
          </button>
          <button
            onClick={() => openWordModal()}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            手动添加生词
          </button>
        </div>
      </div>

      {/* Filter Row: Deck Tabs & Search Input */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        {/* Decks Tab Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
          <button
            onClick={() => {
              setSelectedDeckId('');
              setPage(0);
            }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
              selectedDeckId === ''
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            全部词本
          </button>
          {decks.map((deck) => (
            <button
              key={deck.id}
              onClick={() => {
                setSelectedDeckId(deck.id);
                setPage(0);
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                selectedDeckId === deck.id
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {deck.name}
              {deck.word_count !== undefined && (
                <span className="ml-1 text-[10px] text-slate-400">({deck.word_count})</span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              searchPendingRef.current = true;
              wordsRequestRef.current += 1;
              setLoading(true);
              setSearchQuery(e.target.value);
            }}
            placeholder="搜索单词、中文释义或标签..."
            className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" aria-label="学习状态筛选">
        {([['all', '全部'], ['active', '学习中'], ['parked', '已搁置']] as const).map(([value, label]) => (
          <button key={value} onClick={() => { setParkingFilter(value); setPage(0); }}
            aria-pressed={parkingFilter === value}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${parkingFilter === value ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {label}
          </button>
        ))}
        <span className="text-xs text-slate-500 ml-1">搁置后保留词条和记忆，随时可以恢复。</span>
      </div>

      {/* Words Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-slate-400 text-sm">
            <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            正在加载词条...
          </div>
        ) : words.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Book className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">
              {parkingFilter === 'parked' && !debouncedSearchQuery ? '这里还没有搁置的词' : '没有找到匹配的生词'}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {debouncedSearchQuery ? '可以调整搜索内容、词本或学习状态筛选。'
                : parkingFilter === 'parked' ? '不想背的词可以先搁置，之后在这里恢复学习，词条与记忆进度会保留。'
                : parkingFilter === 'active' ? '可以切换到「全部」或「已搁置」，恢复想继续学习的词。'
                : '可以点击上方「手动添加生词」或前往「生词导入」页面批量上传词库文件。'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="py-3 px-4">单词与音标</th>
                  <th className="py-3 px-4">释义与语言特征</th>
                  <th className="py-3 px-4">所属词本</th>
                  <th className="py-3 px-4">独立熟悉度 (0-5)</th>
                  <th className="py-3 px-4 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {words.map((word) => (
                  <tr key={word.id} className="hover:bg-slate-50/60 transition-colors">
                    {/* Term & Phonetic */}
                    <td className="py-3.5 px-4 font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-900">{word.term}</span>
                        {word.parked && <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">已搁置</span>}
                        <button
                          onClick={() => playAudio(word.term)}
                          title="发音"
                          className="text-slate-400 hover:text-emerald-600 p-0.5 rounded transition-colors"
                        >
                          <Volume2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {word.phonetic && (
                        <span className="text-[11px] font-mono text-slate-400 block mt-0.5">
                          /{word.phonetic.replace(/^\/|\/$/g, '')}/
                        </span>
                      )}
                    </td>

                    {/* Meaning & Rich metadata pills */}
                    <td className="py-3.5 px-4 max-w-xs sm:max-w-md">
                      <p className="text-slate-800 line-clamp-1">{word.meaning || formatExampleText(word.definitions?.[0]) || '-'}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        {word.cefr && (
                          <span className="text-[10px] font-bold px-1.5 py-0.2 bg-indigo-50 text-indigo-700 rounded border border-indigo-200">
                            CEFR {word.cefr}
                          </span>
                        )}
                        {word.parts_of_speech && word.parts_of_speech.length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded">
                            {word.parts_of_speech.join(', ')}
                          </span>
                        )}
                        {word.roots && word.roots.length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.2 bg-emerald-50 text-emerald-700 rounded">
                            根: {word.roots[0]}
                          </span>
                        )}
                        {word.tags && word.tags.map((t) => (
                          <span key={t} className="text-[10px] px-1.5 py-0.2 bg-slate-100 text-slate-500 rounded">
                            {t}
                          </span>
                        ))}
                      </div>
                    </td>

                    {/* Deck */}
                    <td className="py-3.5 px-4 text-slate-600">
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-[11px]">
                        {word.deck_name || (decks.length === 1 ? decks[0].name : '默认词本')}
                      </span>
                    </td>

                    {/* Familiarity (0..5) */}
                    <td className="py-3.5 px-4">
                      {(() => {
                        const lvl = getWordFamiliarityLevel(word);
                        return (
                          <button
                            onClick={() => openFamiliarityModal(word)}
                            title="点击调整独立熟悉度记录"
                            className="flex items-center gap-0.5 text-amber-400 hover:opacity-80"
                          >
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star
                                key={s}
                                className={`w-3.5 h-3.5 ${
                                  (lvl ?? 0) >= s
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-slate-200'
                                }`}
                              />
                            ))}
                            <span className="text-[11px] text-slate-400 ml-1">
                              {lvl !== null ? `${lvl}级` : '未评'}
                            </span>
                          </button>
                        );
                      })()}
                    </td>

                    {/* Actions */}
                    <td className="py-3.5 px-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => handleParking(word)} disabled={parkingWordId !== null || cardActionPending}
                          title={word.parked ? '恢复学习，保留原记忆' : '暂时不想背这个词'}
                          className="px-2 py-1 text-slate-600 hover:bg-amber-50 rounded transition-colors font-medium flex items-center gap-1 disabled:opacity-40">
                          {word.parked ? <PlayCircle className="w-3.5 h-3.5" /> : <PauseCircle className="w-3.5 h-3.5" />}
                          {word.parked ? '恢复' : '搁置'}
                        </button>
                        <button
                          onClick={() => openWordDetail(word.id)}
                          title="卡片与遗忘曲线"
                          className="px-2 py-1 bg-slate-100 hover:bg-emerald-50 text-slate-700 hover:text-emerald-700 rounded transition-colors font-medium flex items-center gap-1"
                        >
                          <Layers className="w-3 h-3" />
                          完整详情
                        </button>
                        <button
                          onClick={() => openWordModal(word)}
                          title="编辑词条"
                          className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {totalWords > pageSize && (
          <div className="px-4 py-3 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between text-xs text-slate-600">
            <span>
              第 {page + 1} / {totalPages} 页 (共 {totalWords} 条)
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="p-1.5 rounded border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="p-1.5 rounded border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-50 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Create Deck */}
      <Modal
        isOpen={createDeckModalOpen}
        onClose={() => setCreateDeckModalOpen(false)}
        title="新建词本"
      >
        <form onSubmit={handleCreateDeck} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              词本名称 *
            </label>
            <input
              type="text"
              required
              value={newDeckName}
              onChange={(e) => setNewDeckName(e.target.value)}
              placeholder="例如：GRE 核心词汇、日常高频阅读"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              说明（可选）
            </label>
            <textarea
              rows={2}
              value={newDeckDesc}
              onChange={(e) => setNewDeckDesc(e.target.value)}
              placeholder="输入该词本的目标或学习计划说明..."
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCreateDeckModalOpen(false)}
              className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-sm"
            >
              创建词本
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: Add/Edit Word with Full Partitioned Fields & JSON mode */}
      <Modal
        isOpen={wordModalOpen}
        onClose={() => setWordModalOpen(false)}
        title={editingWordId ? '编辑词条（保留未知字段）' : '添加生词'}
        maxWidth="max-w-2xl"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 mb-4 border-b border-slate-100">
          <div className="flex rounded-lg bg-slate-100 p-1">
            <button
              type="button"
              onClick={() => setEditMode('form')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                editMode === 'form' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              快捷表单模式
            </button>
            <button
              type="button"
              onClick={() => setEditMode('json')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                editMode === 'json' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              全字段 JSON 模式 (完整结构)
            </button>
          </div>
          <span className="text-[11px] text-slate-400">
            {editMode === 'form' ? '仅提交变更键，自动保留所有对象' : '可直接编辑全部 136 字段与嵌套结构'}
          </span>
        </div>

        {editMode === 'json' ? (
          <form onSubmit={handleSaveWord} className="space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              在此模式下可直接编辑包括 <code>senses</code>、<code>examples</code> 对象数组、<code>collocations</code> 及所有 136 维度的原始 JSON 结构。保存时自动以 <code>If-Match</code> 校验并发版本。
            </p>
            <textarea
              rows={16}
              value={fullJsonText}
              onChange={(e) => setFullJsonText(e.target.value)}
              className="w-full p-3 text-xs font-mono bg-slate-900 text-slate-100 rounded-xl border border-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 leading-relaxed"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setWordModalOpen(false)}
                className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                取消
              </button>
              <button
                type="submit"
                className="px-5 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-sm"
              >
                保存完整 JSON 结构
              </button>
            </div>
          </form>
        ) : (
        <form onSubmit={handleSaveWord} className="space-y-5">
          {/* Section 1: Core Fields */}
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  单词 / 短语 *
                </label>
                <input
                  type="text"
                  required
                  value={wordFormData.term}
                  onChange={(e) => setWordFormData({ ...wordFormData, term: e.target.value })}
                  placeholder="例如：resilient"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  音标 (Phonetic)
                </label>
                <input
                  type="text"
                  value={wordFormData.phonetic}
                  onChange={(e) => setWordFormData({ ...wordFormData, phonetic: e.target.value })}
                  placeholder="例如：rɪˈzɪliənt"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                中文释义 (Meaning)
              </label>
              <textarea
                rows={2}
                value={wordFormData.meaning}
                onChange={(e) => setWordFormData({ ...wordFormData, meaning: e.target.value })}
                placeholder="adj. 有弹性的；能迅速恢复的"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  所属词本
                </label>
                <select
                  value={wordFormData.deck_id}
                  onChange={(e) => setWordFormData({ ...wordFormData, deck_id: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                >
                  <option value="">默认词本</option>
                  {decks.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  分类标签 (Tags，逗号分隔)
                </label>
                <input
                  type="text"
                  value={wordFormData.tagsText}
                  onChange={(e) => setWordFormData({ ...wordFormData, tagsText: e.target.value })}
                  placeholder="GRE, 经济学, 核心词"
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                例句 (Examples，每行一句)
              </label>
              <textarea
                rows={2}
                value={wordFormData.examplesText}
                onChange={(e) => setWordFormData({ ...wordFormData, examplesText: e.target.value })}
                placeholder="He showed great resilience in overcoming the crisis."
                className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Section 2: Collapsible Linguistics & Morphology */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowLinguistics(!showLinguistics)}
              className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-xs font-semibold text-slate-700 transition-colors"
            >
              <span>词汇学与难度（词性 / CEFR / 词根 / 词元）</span>
              {showLinguistics ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {showLinguistics && (
              <div className="p-4 bg-white space-y-3 border-t border-slate-200">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">词性 (parts_of_speech)</label>
                    <input
                      type="text"
                      value={wordFormData.partsOfSpeechText}
                      onChange={(e) => setWordFormData({ ...wordFormData, partsOfSpeechText: e.target.value })}
                      placeholder="adj., n."
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">CEFR 等级</label>
                    <input
                      type="text"
                      value={wordFormData.cefr}
                      onChange={(e) => setWordFormData({ ...wordFormData, cefr: e.target.value })}
                      placeholder="B2 / C1 / C2"
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">原型/词元 (Lemma)</label>
                    <input
                      type="text"
                      value={wordFormData.lemma}
                      onChange={(e) => setWordFormData({ ...wordFormData, lemma: e.target.value })}
                      placeholder="resile"
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">词根词缀 (Roots，逗号分隔)</label>
                  <input
                    type="text"
                    value={wordFormData.rootsText}
                    onChange={(e) => setWordFormData({ ...wordFormData, rootsText: e.target.value })}
                    placeholder="sal/sil (跳), re- (向后/再次)"
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Collapsible Relations & Mnemonics */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowRelations(!showRelations)}
              className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-xs font-semibold text-slate-700 transition-colors"
            >
              <span>近反义词 / 搭配 / 助记与个人笔记</span>
              {showRelations ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {showRelations && (
              <div className="p-4 bg-white space-y-3 border-t border-slate-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">近义词 (Synonyms)</label>
                    <input
                      type="text"
                      value={wordFormData.synonymsText}
                      onChange={(e) => setWordFormData({ ...wordFormData, synonymsText: e.target.value })}
                      placeholder="buoyant, flexible, tough"
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">反义词 (Antonyms)</label>
                    <input
                      type="text"
                      value={wordFormData.antonymsText}
                      onChange={(e) => setWordFormData({ ...wordFormData, antonymsText: e.target.value })}
                      placeholder="fragile, rigid, vulnerable"
                      className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">高频搭配 (Collocations)</label>
                  <input
                    type="text"
                    value={wordFormData.collocationsText}
                    onChange={(e) => setWordFormData({ ...wordFormData, collocationsText: e.target.value })}
                    placeholder="resilient economy, highly resilient"
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">助记法与联想 (Mnemonics)</label>
                  <textarea
                    rows={2}
                    value={wordFormData.mnemonicsText}
                    onChange={(e) => setWordFormData({ ...wordFormData, mnemonicsText: e.target.value })}
                    placeholder="re(再次) + sili(弹) + ent -> 弹回来的 -> 有弹性的"
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1">个人笔记 (Notes)</label>
                  <input
                    type="text"
                    value={wordFormData.notes}
                    onChange={(e) => setWordFormData({ ...wordFormData, notes: e.target.value })}
                    placeholder="常考用法；注意介词搭配 to"
                    className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Collapsible Raw JSON / Extra */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setShowExtraJson(!showExtraJson)}
              className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-100 flex items-center justify-between text-xs font-semibold text-slate-700 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <FileCode className="w-3.5 h-3.5 text-slate-500" />
                自定义扩展 Extra JSON (保留所有未知字段)
              </span>
              {showExtraJson ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {showExtraJson && (
              <div className="p-4 bg-white border-t border-slate-200 space-y-2">
                <p className="text-[11px] text-slate-500">
                  支持直接输入任意扩展 JSON 对象，系统保存时会将其完整存入 `extra` 键，绝不覆盖其他已有字段。
                </p>
                <textarea
                  rows={4}
                  value={wordFormData.extraJson}
                  onChange={(e) => setWordFormData({ ...wordFormData, extraJson: e.target.value })}
                  placeholder={`{\n  "source_book": "The Economist",\n  "custom_priority": 1\n}`}
                  className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setWordModalOpen(false)}
              className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-5 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-sm transition-all"
            >
              {editingWordId ? '保存修改项' : '确认添加'}
            </button>
          </div>
        </form>
        )}
      </Modal>

      {/* Modal: Word Detail with Tabbed Explorer & FSRS Curves */}
      <Modal
        isOpen={detailModalOpen}
        onClose={closeWordDetail}
        title={selectedWordDetail ? `完整词条：${selectedWordDetail.term}` : '词条详情'}
        maxWidth="max-w-3xl"
      >
        {detailLoading || !selectedWordDetail ? (
          <div className="py-12 text-center text-sm text-slate-400">
            {detailLoading ? '正在获取详情...' : '无法读取词条详情，请关闭后重试。'}
          </div>
        ) : (
          <div className="space-y-5">
            {/* Word Header */}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-2xl font-bold text-slate-900">{selectedWordDetail.term}</h4>
                    {selectedWordDetail.cefr && (
                      <span className="text-xs font-bold px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded">
                        CEFR {selectedWordDetail.cefr}
                      </span>
                    )}
                  </div>
                  {selectedWordDetail.phonetic && (
                    <span className="text-xs font-mono text-slate-500">
                      /{selectedWordDetail.phonetic}/
                    </span>
                  )}
                </div>
                <button
                  onClick={() => playAudio(selectedWordDetail.term)}
                  className="p-2 bg-white rounded-lg border border-slate-200 text-slate-600 hover:text-emerald-700 shadow-sm"
                >
                  <Volume2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm font-semibold text-slate-800 mt-2">
                {selectedWordDetail.meaning || formatExampleText(selectedWordDetail.definitions?.[0]) || '暂无释义'}
              </p>
            </div>

            {/* Navigation Tabs */}
            <div className="flex border-b border-slate-200 text-xs font-medium gap-2">
              <button
                onClick={() => setDetailTab('core')}
                className={`pb-2.5 px-3 border-b-2 transition-colors ${
                  detailTab === 'core'
                    ? 'border-emerald-600 text-emerald-800 font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                例句与释义
              </button>
              <button
                onClick={() => setDetailTab('linguistics')}
                className={`pb-2.5 px-3 border-b-2 transition-colors ${
                  detailTab === 'linguistics'
                    ? 'border-emerald-600 text-emerald-800 font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                词源形态
              </button>
              <button
                onClick={() => setDetailTab('relations')}
                className={`pb-2.5 px-3 border-b-2 transition-colors ${
                  detailTab === 'relations'
                    ? 'border-emerald-600 text-emerald-800 font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                关联与助记
              </button>
              <button
                onClick={() => setDetailTab('cards')}
                className={`pb-2.5 px-3 border-b-2 transition-colors ${
                  detailTab === 'cards'
                    ? 'border-emerald-600 text-emerald-800 font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                复习卡片与曲线 ({selectedWordDetail.cards?.length || 0})
              </button>
              <button
                onClick={() => setDetailTab('json')}
                className={`pb-2.5 px-3 border-b-2 transition-colors ${
                  detailTab === 'json'
                    ? 'border-emerald-600 text-emerald-800 font-bold'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                完整 JSON 字段
              </button>
            </div>

            {/* Tab 1: Core */}
            {detailTab === 'core' && (
              <div className="space-y-4 text-xs">
                <div>
                  <h6 className="font-semibold text-slate-700 uppercase tracking-wider mb-2">
                    例句与搭配
                  </h6>
                  {selectedWordDetail.examples && selectedWordDetail.examples.length > 0 ? (
                    <ul className="space-y-2">
                      {selectedWordDetail.examples.map((ex, i) => (
                        <li key={i} className="p-3 bg-slate-50 rounded-lg text-slate-700 italic border border-slate-100">
                          "{formatExampleText(ex)}"
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-slate-400">暂无例句</p>
                  )}
                </div>

                {selectedWordDetail.tags && selectedWordDetail.tags.length > 0 && (
                  <div>
                    <h6 className="font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                      标签
                    </h6>
                    <div className="flex flex-wrap gap-1">
                      {selectedWordDetail.tags.map((t) => (
                        <span key={t} className="px-2 py-0.5 bg-slate-100 rounded text-slate-600">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab 2: Linguistics */}
            {detailTab === 'linguistics' && (
              <div className="space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg">
                  <div>
                    <span className="text-slate-400">词性:</span>{' '}
                    <span className="font-medium text-slate-700">
                      {selectedWordDetail.parts_of_speech?.join(', ') || '未指定'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400">原型/词元:</span>{' '}
                    <span className="font-medium text-slate-700 font-mono">
                      {selectedWordDetail.lemma || selectedWordDetail.term}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400">词根:</span>{' '}
                    <span className="font-medium text-slate-700">
                      {selectedWordDetail.roots?.join(', ') || '无'}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400">词形变换:</span>{' '}
                    <span className="font-medium text-slate-700 font-mono">
                      {selectedWordDetail.forms ? JSON.stringify(selectedWordDetail.forms) : '无'}
                    </span>
                  </div>
                </div>
                {selectedWordDetail.etymology && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="font-semibold text-slate-700 block mb-1">词源 (Etymology):</span>
                    <p className="text-slate-600 leading-relaxed">{selectedWordDetail.etymology}</p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 3: Relations */}
            {detailTab === 'relations' && (
              <div className="space-y-3 text-xs">
                {selectedWordDetail.synonyms && selectedWordDetail.synonyms.length > 0 && (
                  <div>
                    <span className="font-semibold text-slate-700 block mb-1">近义词 (Synonyms):</span>
                    <div className="flex flex-wrap gap-1">
                      {selectedWordDetail.synonyms.map((s) => (
                        <span key={s} className="px-2 py-0.5 bg-emerald-50 text-emerald-800 rounded">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selectedWordDetail.antonyms && selectedWordDetail.antonyms.length > 0 && (
                  <div>
                    <span className="font-semibold text-slate-700 block mb-1">反义词 (Antonyms):</span>
                    <div className="flex flex-wrap gap-1">
                      {selectedWordDetail.antonyms.map((a) => (
                        <span key={a} className="px-2 py-0.5 bg-red-50 text-red-800 rounded">
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {selectedWordDetail.mnemonics && selectedWordDetail.mnemonics.length > 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                    <span className="font-semibold text-amber-900 block mb-1">记忆助记 (Mnemonics):</span>
                    <ul className="list-disc list-inside text-amber-800 space-y-0.5">
                      {selectedWordDetail.mnemonics.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedWordDetail.notes && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <span className="font-semibold text-slate-700 block mb-1">备忘笔记:</span>
                    <p className="text-slate-600">{selectedWordDetail.notes}</p>
                  </div>
                )}

                {selectedWordDetail.familiarity && Array.isArray(selectedWordDetail.familiarity) && selectedWordDetail.familiarity.length > 0 && (
                  <div className="p-3 bg-amber-50/60 border border-amber-200/60 rounded-xl space-y-2">
                    <span className="font-semibold text-amber-900 block text-xs">
                      独立熟悉度历史 ({selectedWordDetail.familiarity.length} 次标记):
                    </span>
                    <div className="space-y-1.5 max-h-36 overflow-y-auto">
                      {selectedWordDetail.familiarity.map((rec, i) => (
                        <div key={rec.id || i} className="flex items-center justify-between text-[11px] bg-white p-2 rounded border border-amber-100">
                          <div className="flex items-center gap-1 text-amber-600 font-bold">
                            <span>{rec.level} 级</span>
                            {rec.note && <span className="text-slate-600 font-normal ml-2">"{rec.note}"</span>}
                          </div>
                          <span className="text-slate-400 font-mono text-[10px]">
                            {rec.created_at ? new Date(rec.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tab 4: Cards & Curves */}
            {detailTab === 'cards' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 font-medium">
                    关联卡片 ({selectedWordDetail.cards?.length || 0})
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAddCardForWord('recognition')}
                      disabled={cardActionPending || parkingWordId !== null}
                      className="text-[11px] font-medium text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded border border-emerald-200"
                    >
                      + 识别卡片
                    </button>
                    <button
                      onClick={() => handleAddCardForWord('production')}
                      disabled={cardActionPending || parkingWordId !== null}
                      className="text-[11px] font-medium text-blue-700 hover:bg-blue-50 px-2 py-1 rounded border border-blue-200"
                    >
                      + 拼写卡片
                    </button>
                  </div>
                </div>

                {selectedWordDetail.cards && selectedWordDetail.cards.length > 0 ? (
                  <div className="space-y-3">
                    {selectedWordDetail.cards.map((c) => (
                      <div
                        key={c.id}
                        className="p-3 bg-white border border-slate-200 rounded-lg text-xs space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-slate-800">
                            {c.template === 'production' ? '产出/拼写模式' : '英汉识别模式'}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => loadCardCurve(c.id)}
                              className="text-blue-600 hover:underline"
                            >
                              查看遗忘曲线
                            </button>
                            <button
                              onClick={() => handleToggleCardSuspend(c.id, c.suspended)}
                              disabled={cardActionPending || parkingWordId !== null}
                              className="flex items-center gap-1 text-slate-500 hover:text-slate-800"
                            >
                              {c.suspended ? (
                                <>
                                  <PlayCircle className="w-3.5 h-3.5 text-emerald-600" />
                                  <span>恢复学习</span>
                                </>
                              ) : (
                                <>
                                  <PauseCircle className="w-3.5 h-3.5 text-amber-600" />
                                  <span>搁置卡片</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-slate-500 bg-slate-50 p-2 rounded">
                          <div>
                            稳定性 S: <span className="font-mono font-semibold text-slate-700">{c.stability ? c.stability.toFixed(1) : '-'}天</span>
                          </div>
                          <div>
                            难度 D: <span className="font-mono font-semibold text-slate-700">{c.difficulty ? c.difficulty.toFixed(1) : '-'}</span>
                          </div>
                          <div>
                            复习次数: <span className="font-mono font-semibold text-slate-700">{c.reps || 0}</span>
                          </div>
                          <div>
                            遗忘次数: <span className="font-mono font-semibold text-slate-700">{c.lapses || 0}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 py-2">当前词条尚未关联卡片</p>
                )}

                {activeCardCurve && (
                  <div className="pt-2">
                    <RetentionCurveChart curve={activeCardCurve} />
                  </div>
                )}
              </div>
            )}

            {/* Tab 5: Raw Full JSON Explorer */}
            {detailTab === 'json' && (
              <div>
                <pre className="p-3 bg-slate-900 text-slate-100 rounded-xl text-[11px] font-mono max-h-72 overflow-y-auto leading-relaxed">
                  {JSON.stringify(selectedWordDetail, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal: Familiarity Rating */}
      <Modal
        isOpen={familiarityModalOpen}
        onClose={() => setFamiliarityModalOpen(false)}
        title="独立熟悉度记录"
      >
        <form onSubmit={handleSaveFamiliarity} className="space-y-4">
          <p className="text-xs text-slate-500 leading-relaxed">
            独立熟悉记录用于学习者自主主观打标（0~5 级），纯粹用于个人词汇量掌控与筛选，<strong>不会伪造或影响 FSRS 间隔卡片调度算法</strong>。
          </p>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">
              主观熟练等级 (0: 完全陌生 ~ 5: 熟记如母语)
            </label>
            <div className="flex items-center gap-2">
              {[0, 1, 2, 3, 4, 5].map((lvl) => (
                <button
                  key={lvl}
                  type="button"
                  onClick={() => setFamiliarityLevel(lvl)}
                  className={`w-9 h-9 rounded-lg font-bold text-xs transition-all ${
                    familiarityLevel === lvl
                      ? 'bg-amber-500 text-white shadow-md shadow-amber-200'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {lvl}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              备忘或个人记忆线索（可选）
            </label>
            <input
              type="text"
              value={familiarityNote}
              onChange={(e) => setFamiliarityNote(e.target.value)}
              placeholder="例如：常在科技论文中见到，已掌握其衍生词"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setFamiliarityModalOpen(false)}
              className="px-4 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              取消
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 shadow-sm"
            >
              保存熟悉记录
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
